import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AutoComplete, Input } from "antd";
import VaporAccordion from "../../../vapor/accordion";
import VaporChip from "../../../vapor/chip";
import { FormattedMessage } from "react-intl";
import Table from "../../../vapor/tableComplex";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getFixedAssetsList } from "../../../../store/actions/precog/assets";
import { VaporDatePicker } from "../../../vapor";
import numeral from "numeral";
import InfiniteScroll from "react-infinite-scroller";
import moment from "moment";

/**
 * Custom hooks to manage filters in this drawer
 * @returns [filters, handleChange, resetDate, clearFilters]
 */
const useFilters = () => {
  const [filters, setFilters] = useState({
    dateFrom: null,
    dateTo: null,
    supplier: "",
    description: ""
  });

  const handleChange = (name, value) => {
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const resetDate = () => {
    setFilters({
      ...filters,
      dateFrom: null,
      dateTo: null
    });
  };

  const clearFilters = (asset) => {
    setFilters({
      dateFrom: null,
      dateTo: null,
      supplier: "",
      description: asset
    });
  };

  return [filters, handleChange, resetDate, clearFilters];
};

/**
 * Drawer to search and select an existing asset
 * @param {*} param0
 * @returns
 */
const SearchAssetsDrawerSection = ({ businessId, selectAsset, setDisableSaveButton }) => {
  const dispatch = useDispatch();

  const [accordion, setAccordion] = useState([true, true]); // Status of accordion (Open/Close)
  const [assetsList, setAssetsList] = useState([]);
  const [filters, setFilters, resetDate, clearFilters] = useFilters(); // Custom hook to manage filters
  const [showFilters, setShowFilters] = useState(false); // Show/Hide filters chips section
  const [visibleFilters, setVisibleFilters] = useState(null); // Save here filters value visible in chips
  const [cancelSearch, setCancelSearch] = useState(false); // Set if show "search" icon or "x" icon in the top input field
  const [autocompleteList, setAutocompleteList] = useState([]); // Autocomplete list
  const [delayAutocomplete, setDelayAutocomplete] = useState(null); // Delay autocomplete
  const [selectPressed, setSelectPressed] = useState(false); // Set if an option is clicked in the autocomplete list
  const [nextLink, setNextLink] = useState(null);
  const [isLoadingList, setIsLoadingList] = useState(false);

  const dateFormatList = ["DD/MM/YYYY", "DD/MM/YY", "DD-MM-YYYY", "DD-MM-YY", "DDMMYYYY", "DDMMYY"];

  // Columns for table header
  const columns = [
    {
      dataIndex: "radiobutton",
      key: "radiobutton",
      title: ""
    },
    {
      dataIndex: "supplier",
      key: "supplier",
      title: "Fornitore"
    },
    {
      dataIndex: "description",
      key: "description",
      title: "Descrizione"
    },
    {
      dataIndex: "alias",
      key: "alias",
      title: "Cod. identificativo"
    },
    {
      dataIndex: "purchaseDate",
      key: "purchaseDate",
      title: "Data acquisto"
    },
    {
      dataIndex: "amount",
      key: "amount",
      title: "Totale",
      render: (val) => { return `${numeral(val).format("0,0.00")} €`;}
    }
  ];

  /**
     * Function to get assets list based on filters, if newFilters are not passed use global filters
     * @param {*} newFilters
     */
  const handleSearch = (newFilters = null) => {
    setIsLoadingList(true);
    let filtersUsed = newFilters ? newFilters : filters;
    if (filtersUsed.description !== ""){
      setCancelSearch(true);
    } else {
      
      setDisableSaveButton(true);
      setCancelSearch(false);
    }
      
    if (filtersUsed.dateFrom || filtersUsed.dateTo || filtersUsed.supplier || filtersUsed.description) {
      setVisibleFilters(filtersUsed);

      let dataToDispatch = {
        ...filtersUsed,
        status: "ACTIVE"
      };
      if (filtersUsed.dateFrom && filtersUsed.dateTo)
        dataToDispatch = {
          ...filtersUsed,
          purchaseDateFrom: filtersUsed.dateFrom ? filtersUsed.dateFrom.format("YYYY-MM-DD") : null,
          purchaseDateTo: filtersUsed.dateTo ? filtersUsed.dateTo.format("YYYY-MM-DD") : null          
        };
      const cloneData = {...dataToDispatch};
      delete cloneData.dateFrom;
      delete cloneData.dateTo;      
      
      dispatch(getFixedAssetsList(businessId, cloneData))
        .then(res => {                    
          const isFiltersVisible = Object.keys(filtersUsed).length > 0 && ((filtersUsed.dateFrom && filtersUsed.dateTo) || filtersUsed.supplier);
          setShowFilters(isFiltersVisible);
          setAssetsList(res._embedded.elements);          
          setNextLink(res?._links?.next?.href);
          setAccordion([false, false]);
          setIsLoadingList(false);
        });
    } else {
      setIsLoadingList(false);
      setNextLink(null);
      clearFilters(filters.description);
      setShowFilters(false);
      setAssetsList([]);
      
      setDisableSaveButton(true);
    }
  };

  useEffect(() => {
    if (filters.dateFrom && filters.dateTo)
      handleSearch();
  }, [filters.dateFrom, filters.dateTo]);

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      selectAsset(selectedRows[0]);   
               
      setDisableSaveButton(false);
    }
  };

  /**
     * Function to render chips with filters values and "Annula filtri" button
     * @returns {JSX}
     */
  const renderFilters = () => {
    if (showFilters)
      return <div className="drawer-search-filters-chips">
        <span className="drawer-search-filters-cancel" onClick={() => {
          handleSearch({ description: filters.description });
        }}>Annulla filtri</span>
        {visibleFilters.supplier && <VaporChip
          onClick={() => {
            setFilters("supplier", "");
            setVisibleFilters({ ...visibleFilters, supplier: "" });
            handleSearch({ ...visibleFilters, supplier: "" });
          }}
          title={
            <React.Fragment>
              <span className="light">Fornitore</span>
              {visibleFilters.supplier}
              <span className="icon"></span>
            </React.Fragment>
          }
          active={true}
          style={{
            borderRadius: "0px",
            border: "unset",
            fontWeight: "600",
            paddingRight: "35px",
            position: "relative"
          }}
        />}
        {(visibleFilters.dateFrom || visibleFilters.dateTo) &&
          <VaporChip
            onClick={() => {
              resetDate();
              setVisibleFilters({ ...visibleFilters, dateFrom: null, dateTo: null });
              handleSearch({ ...visibleFilters, dateFrom: null, dateTo: null });
            }}
            title={
              <React.Fragment>
                <span className="light">Data</span>
                {`${visibleFilters.dateFrom ? visibleFilters.dateFrom.format("DD/MM/YYYY") : " * "} - ${visibleFilters.dateTo ? visibleFilters.dateTo.format("DD/MM/YYYY") : " * "}`}
                <span className="icon" ></span>
              </React.Fragment>}
            active={true}
            style={{
              borderRadius: "0px",
              border: "unset",
              fontWeight: "600",
              paddingRight: "35px",
              position: "relative"
            }}
          />}

      </div>;

    return undefined;
  };

  const handleAutocomplete = (value) => {

    if (value.length >= 3) {
      if (delayAutocomplete)
        clearTimeout(delayAutocomplete);
      
      setDelayAutocomplete(setTimeout(dispatch(getFixedAssetsList(businessId, {
        description: value,
        status: "ACTIVE"
      }))
        .then(res => {
          const newAutocompleteList = res?._embedded?.elements ?
            ([...new Set(res?._embedded?.elements?.map(asset => asset.description))]).map(x => { return { value: x, label: x }; })
            : [];
          setAutocompleteList(newAutocompleteList);
        })), 500);
    } else {
      setAutocompleteList([]);
      setDelayAutocomplete(null);
    }
  };
  
  return (
    <div className="search-assets-drawer-section">
      <div className="drawer-search-header">
        <div className="drawer-search-text">
          <FormattedMessage id={"preCog.assetsTab.drawer.searchExistingAsset"} />
        </div>
        <AutoComplete
          className="drawer-search-autocomplete"
          options={autocompleteList}
          value={filters.description}
          open={autocompleteList && autocompleteList.length > 0 && !selectPressed ? true : false}
          onSelect={value => {
            setFilters("description", value);
            setSelectPressed(true);
            setAutocompleteList([]);
            handleSearch({...filters, "description": value});
          }}
          onFocus={() => {
            setSelectPressed(false);
            setAutocompleteList([]);
          }}
          onChange={(value) => {
            setFilters("description", value);
            handleAutocomplete(value);
            setCancelSearch(false);
            if (value !== filters.description)
              setSelectPressed(false);            
          }}
          onBlur={() => {
            setAutocompleteList([]);
            setDelayAutocomplete(null);
          }}
          getPopupContainer={trigger => trigger.parentNode}

        >
          <Input.Search
            size="large"
            enterButton={cancelSearch ? <FontAwesomeIcon icon={["far", "times"]} style={{ width: "14px" }} /> : <FontAwesomeIcon icon={["fal", "search"]} />
            }
            onSearch={() => {                        
              if (cancelSearch && filters.description) {                
                handleSearch({ ...filters, description: "" });
                setVisibleFilters({ ...visibleFilters, description: "" });
                setFilters("description", "");       
                selectAsset(null);         
              } else
                handleSearch();
            }}
          />
        </AutoComplete>
      </div>
      <div className="drawer-search-body">
        <div className="drawer-search-filter-title">
          <FormattedMessage id={"preCog.assetsTab.drawer.filterResults"} />
        </div>
        {renderFilters()}
        <VaporAccordion
          onChange={val => setAccordion([!accordion[0], accordion[1]])}
          opened={accordion[0]}
          title={<FormattedMessage id={"preCog.assetsTab.drawer.date"} />}
        >
          <div className="drawer-search-accordion">
            <div className="drawer-search-filter-col">
              <p className="drawer-search-text">Da</p>
              <VaporDatePicker
                getPopupContainer={(trigger) => trigger.parentNode}
                onChange={(date) => {
                  setFilters("dateFrom", date);
                }}
                format={dateFormatList}
                value={filters.dateFrom ? moment(filters.dateFrom, dateFormatList[0]) : undefined}
                disabledDate={(current) => {
                  if (!filters.dateTo) return false;
                  return current && current >= filters.dateTo;
                }}
                style={!filters.dateFrom && filters.dateTo ? { boxShadow: "0 0 0 2px red" } : {}}
              />
            </div>
            <div className="drawer-search-filter-col">
              <p className="drawer-search-text">A</p>
              <VaporDatePicker
                getPopupContainer={(trigger) => trigger.parentNode}
                onChange={(date) => {
                  setFilters("dateTo", date);
                }}
                format={dateFormatList}
                disabledDate={(current) => {
                  if (!filters.dateFrom) return false;
                  return current && current <= filters.dateFrom;
                }}
                value={filters.dateTo ? moment(filters.dateTo, dateFormatList[0]) : undefined}
                style={filters.dateFrom && !filters.dateTo ? { boxShadow: "0 0 0 2px red" } : {}}
              />
            </div>
          </div>
        </VaporAccordion>
        <VaporAccordion
          onChange={val => setAccordion([accordion[0], !accordion[1]])}
          opened={accordion[1]}
          title={<FormattedMessage id={"preCog.assetsTab.drawer.supplier"} />}
        >
          <div className="drawer-search-accordion">
            <div className="drawer-search-filter-col">
              <div className="drawer-search-text">
                <FormattedMessage id={"preCog.assetsTab.drawer.lookForSupplier"} />
              </div>
              <AutoComplete
                className="drawer-search-autocomplete longer"
                value={filters.supplier}
                onChange={(value) => {
                  setFilters("supplier", value);
                }}
              >
                <Input.Search
                  size="large"
                  enterButton={
                    <FontAwesomeIcon icon={["fal", "search"]} />
                  }
                  onSearch={() => handleSearch()}
                />
              </AutoComplete>
            </div>
          </div>
        </VaporAccordion>
        {assetsList && assetsList.length > 0 &&
          <React.Fragment>
            <div className="drawer-search-filter-title">
              <FormattedMessage id={"preCog.assetsTab.drawer.results"} />
            </div>
            
            <InfiniteScroll
              initialLoad={false}
              pageStart={1}    
              loadMore={() => {    
                setIsLoadingList(true);                       
                dispatch(getFixedAssetsList(
                  businessId, null, nextLink
                ))
                  .then(res => {                                               
                    setAssetsList(assetsList.concat(res._embedded.elements));          
                    setNextLink(res?._links?.next?.href);                    
                    setIsLoadingList(false);
                  }).catch(e => console.log(e));                
              }}
              threshold={-400}
              hasMore={!!nextLink}
              loader={
                isLoadingList &&
                (<div
                  style={{ position: "fixed", top: "50%", 
                    backgroundColor: "rgba(256,256,256, 0.7)", 
                    width: "50%",
                    height: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center", fontSize: "32px", color: "#0090d1" }}                  
                >
                  <FontAwesomeIcon icon={["far", "circle-notch"]} spin />
                </div>)
              }
              useWindow={false}    
              getScrollParent={() =>                 
                document.querySelector(".vapor-drawer-container")
              }            
            >   
              <Table
                rowSelection={{
                  type: "radio",
                  ...rowSelection
                }}
                columns={columns}
                dataSource={assetsList}
              />
            </InfiniteScroll>
            {
              assetsList.map(x => x.radiobutton)
            }
          </React.Fragment>}

      </div>
    </div>
  );
};

export default SearchAssetsDrawerSection;
